import posthog, { type CaptureResult } from 'posthog-js';
import { resolveDistinctId } from '@evaltick/shared';

import { FEATURES } from '@/constants';
import { ROUTES } from '@/routes';
import type { BackendUser } from '@/types';

/**
 * Browser-side PostHog: identifies the user, so browser and API events land on
 * the same person, and captures pageviews for Web Analytics. It sends no custom
 * events.
 *
 * Needs both the flag and the key, like the API. The only PostHog project is
 * production, so a key copied into .env.local alone does nothing.
 */
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

const isEnabled = (): boolean => FEATURES.ANALYTICS && POSTHOG_KEY.length > 0;

/**
 * Session replay is the minimal safe version, scaffolding only: everything
 * masked, a few routes blocked, off behind a flag. What should actually be
 * recorded, and how much text to unmask, is for a future ticket.
 *
 * Never recorded: admin pages, including /evaluate (other candidates' results),
 * HTS pages (answers we encrypt at rest), and /register (its URL holds a
 * sign-up ticket, and recordings keep URLs unmasked).
 */
const REPLAY_BLOCKED_PREFIXES = [ROUTES.ADMIN, ROUTES.EVALUATE, ROUTES.HTS_QUESTIONNAIRE, ROUTES.REGISTER];

const isReplayAllowed = (pathname: string): boolean =>
  !REPLAY_BLOCKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// Query param carrying the invite link's Clerk sign-up token (see Register.tsx).
const CLERK_TICKET_PARAM = '__clerk_ticket';

// URL properties PostHog attaches to events and person records.
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];

const withoutQuery = (value: string): string => {
  try {
    const url = new URL(value);
    url.search = '';
    return url.toString();
  } catch {
    // `$referrer` can be "$direct".
    return value;
  }
};

/**
 * Drops the query string from every URL on every event, so the invite link's
 * sign-up ticket never reaches PostHog. No page needs its query for analytics.
 */
const stripQueryStrings = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue;
    for (const key of URL_PROPERTIES) {
      if (typeof bag[key] === 'string') bag[key] = withoutQuery(bag[key]);
    }
  }
  return event;
};

/** Starts or stops recording to match the current route. */
const syncReplayToRoute = (): void => {
  if (isReplayAllowed(window.location.pathname)) {
    if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording();
  } else {
    // Unconditional: sessionRecordingStarted() reads false while the recorder
    // is still loading after a start, and stopping when idle is a no-op.
    posthog.stopSessionRecording();
  }
};

export const initAnalytics = (): void => {
  if (!isEnabled()) return;

  // The PostHog project settings switch on replay, console logs, exceptions,
  // heatmaps and dead clicks. Each is set explicitly here so the browser
  // doesn't depend on those settings.
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Pageviews on client-side navigation, plus $pageleave for bounce rate.
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    autocapture: false,
    rageclick: false,
    capture_performance: false,
    capture_exceptions: false,
    capture_dead_clicks: false,
    enable_heatmaps: false,
    before_send: stripQueryStrings,
    // The flags request skips before_send, so the ticket is also masked where
    // the SDK first reads the URL.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [CLERK_TICKET_PARAM],
    // Replay only starts through syncReplayToRoute.
    disable_session_recording: true,
    // Replay masking doesn't cover console output: never log candidate text.
    enable_recording_console_log: FEATURES.SESSION_REPLAY,
    session_recording: {
      // Mask all text, not just inputs: pages show answers, feedback and
      // scores. Masking happens in the browser.
      maskTextSelector: '*',
      maskAllInputs: true,
      // API responses carry answers and scores.
      recordHeaders: false,
      recordBody: false,
    },
  });

  if (FEATURES.SESSION_REPLAY) {
    syncReplayToRoute();
    // A pageview fires on every navigation, so it doubles as a route change.
    posthog.on('eventCaptured', (event: CaptureResult) => {
      if (event.event === '$pageview') syncReplayToRoute();
    });
  }
};

/**
 * Links this browser to the person the API reports on. Guests get no name or
 * email here: theirs are synthetic, and the API names them at redemption.
 */
export const identifyUser = (user: BackendUser, { isGuest }: { isGuest: boolean }): void => {
  if (!isEnabled()) return;

  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  posthog.identify(
    resolveDistinctId(user),
    isGuest
      ? { role: user.role, isGuest }
      : {
          email: user.email,
          name: name || undefined,
          firstName: user.firstName ?? undefined,
          lastName: user.lastName ?? undefined,
          role: user.role,
          isGuest,
        }
  );
};

/**
 * Forgets the user on sign-out. Skipped for anonymous visitors, where it would
 * only discard the id their pre-login pageviews are filed under.
 */
export const resetAnalytics = (): void => {
  if (!isEnabled()) return;
  if (posthog.get_property('$user_state') !== 'identified') return;
  posthog.reset();
};
